import { matchPath } from "react-router-dom";
import type { Capability, CapabilityNeed } from "./capabilities";

// The capability each authenticated route needs, as ONE table. Both consumers
// read it: the route guard (app/CapabilityRoute) redirects a user who lands on a
// path they cannot use, and the sidebar (components/layout/nav) hides the leaves
// pointing at those paths. Stating it twice is how a screen ends up hidden from
// the menu but reachable by URL, or gone from the menu while still working.
//
// The capability chosen is the READ capability of the context the screen opens,
// matching the GET route the page's first request hits - so the gate and the
// server's 403 agree. Actions INSIDE a page (issue, refund, adjust stock) are
// separate capabilities the components gate themselves with useAuth().can.
//
// Keys are route patterns (matchPath), so detail routes gate with their list.
// Routes deliberately absent are open to every signed-in user:
//   /app            the dashboard - the redirect target, so it must never gate
//   /app/account    your own profile
//   /app/settings   a mix of tenant admin and personal tabs; gated per-tab
//                   inside SettingsPage instead of all-or-nothing here
const ROUTE_CAPABILITY: Record<string, CapabilityNeed> = {
  // Selling opens a draft invoice, so it needs the same authority as writing one.
  "/app/sales": "invoice:write",

  "/app/jobs": "jobcard:read",
  "/app/jobs/:id": "jobcard:read",

  "/app/customers": "customer:read",
  "/app/customers/:id": "customer:read",
  "/app/vehicles": "customer:read",
  "/app/vehicles/:id": "customer:read",

  "/app/parts": "inventory:read",
  "/app/catalog": "inventory:read",
  "/app/labels": "inventory:read",
  // Bulk import writes the catalog; reading it is not enough.
  "/app/import": "inventory:manage",

  "/app/invoices": "invoice:read",
  "/app/invoices/:id": "invoice:read",
  "/app/quotes": "invoice:read",
  "/app/quotes/:id": "invoice:read",

  // Procurement is one screen over two contexts (orders + suppliers): either
  // read gets you in, and the tabs inside show what you hold.
  "/app/procurement": ["po:read", "supplier:read"],
  "/app/procurement/orders/:id": "po:read",
  "/app/procurement/suppliers/:id": "supplier:read",

  "/app/expenses": "expense:read",
  "/app/expenses/categories": "expense:manage",

  "/app/payroll": "payroll:read",
  "/app/payroll/runs": "payroll:read",

  // The daybook is a report over every financial movement (the backend gates
  // GET /billing/accounting/daybook on report:read), not the double-entry ledger.
  "/app/ledger": "report:read",
  "/app/ledger/movements": "report:read",
  "/app/accounting": "accounting:read",
  "/app/reports": "report:read",

  "/app/printers": "printer:read",
};

/**
 * The capability a path needs, or undefined when it is open to any signed-in
 * user. Static paths hit the table directly; a detail URL is matched against the
 * patterns, longest-literal first so "/app/procurement/orders/:id" wins over a
 * pattern that would also match it.
 */
export function capabilityForPath(pathname: string): CapabilityNeed | undefined {
  const direct = ROUTE_CAPABILITY[pathname];
  if (direct) return direct;
  const hit = PATTERNS.find((p) => matchPath({ path: p, end: true }, pathname));
  return hit ? ROUTE_CAPABILITY[hit] : undefined;
}

// Patterns with more literal segments first: matchPath itself does not rank, and
// the first match wins.
const PATTERNS = Object.keys(ROUTE_CAPABILITY).sort(
  (a, b) => b.split("/").filter((s) => !s.startsWith(":")).length - a.split("/").filter((s) => !s.startsWith(":")).length,
);

/** Re-export for the roles editor / tests that want the whole table. */
export type { Capability, CapabilityNeed };
